// contexts/CorporateAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getAuthToken } from '@/lib/authToken';

// Types for Corporate Authentication
interface CorporateUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'corporate_owner' | 'limited_admin' | 'admin' | 'manager' | 'member' | 'viewer';
  authMethod: 'email_password' | 'six_digit_code' | 'sso';
  corporateAccount: CorporateAccount;
  permissions: Permission[];
  department?: string;
  avatar?: string;
  isActive: boolean;
}

interface CorporateAccount {
  id: string;
  companyName: string;
  companyDomain: string;
  industry?: string;
  companySize?: string;
  subscription: {
    plan: 'basic' | 'professional' | 'enterprise';
    status: 'active' | 'suspended' | 'cancelled';
  };
  billing: {
    accountBalance: number;
    currentUserCount: number;
    maxUsers: number;
  };
  ssoConfiguration?: {
    enabled: boolean;
    defaultProvider: string;
  };
}

interface Permission {
  resource: string;
  actions: string[];
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  department?: string;
  token: string;
  expiresAt: string;
  isUsed: boolean;
  createdAt: string;
}

interface JoinCode {
  id: string;
  code: string;
  role: string;
  department?: string;
  maxUses: number;
  currentUses: number;
  expiresAt: string;
  isActive: boolean;
}

interface AuthResult {
  success: boolean;
  user?: CorporateUser;
  token?: string;
  message?: string;
  requiresVerification?: boolean;
}

interface BillingResult {
  success: boolean;
  balance?: number;
  transaction?: any;
  message?: string;
}

interface UserResult {
  success: boolean;
  users?: CorporateUser[];
  user?: CorporateUser;
  message?: string;
}

// Corporate Authentication Context Interface
interface CorporateAuthContextType {
  // Authentication State
  user: CorporateUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Authentication Methods
  loginWithPassword: (email: string, password: string) => Promise<AuthResult>;
  loginWithCode: (email: string, code: string) => Promise<AuthResult>;
  requestAccessCode: (email: string) => Promise<{ success: boolean; message?: string }>;
  acceptInvitation: (token: string) => Promise<AuthResult>;
  joinWithCode: (code: string) => Promise<AuthResult>;
  ssoLogin: (provider: string) => Promise<{ success: boolean; redirectUrl?: string }>;
  logout: () => Promise<void>;
  
  // Corporate Management
  createInvitation: (email: string, role: string, department?: string) => Promise<Invitation>;
  generateJoinCode: (role: string, department?: string, options?: any) => Promise<JoinCode>;
  manageBilling: (action: string, params?: any) => Promise<BillingResult>;
  manageUsers: (action: string, userIds?: string[], data?: any) => Promise<UserResult>;
  deactivateUser: (userId: string) => Promise<{ success: boolean; message?: string }>;
  activateUser: (userId: string) => Promise<{ success: boolean; message?: string }>;
  deleteUser: (userId: string) => Promise<{ success: boolean; message?: string }>;
  
  // Corporate Account Management
  getCorporateAccount: () => Promise<CorporateAccount | null>;
  updateCorporateSettings: (settings: any) => Promise<{ success: boolean }>;
  getInvitations: () => Promise<Invitation[]>;
  deleteInvitation: (id: string) => Promise<void>;
  getJoinCodes: () => Promise<JoinCode[]>;
  getUsersList: () => Promise<CorporateUser[]>;
  
  // Permissions & Role Checking
  hasPermission: (resource: string, action: string) => boolean;
  isRole: (role: string) => boolean;
  canManageUsers: () => boolean;
  canManageBilling: () => boolean;
  canCreateTeams: () => boolean;
}

// Create Context
const CorporateAuthContext = createContext<CorporateAuthContextType | undefined>(undefined);

// API Base URL
const API_BASE = '/api';

// Corporate Authentication Provider Component
export const CorporateAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<CorporateUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Initialize authentication state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('corporate_token');
      if (token) {
        const response = await fetch(`${API_BASE}/auth/verify-token`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData.user);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('corporate_token');
        }
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      localStorage.removeItem('corporate_token');
    } finally {
      setIsLoading(false);
    }
  };

  // ============== AUTHENTICATION METHODS ==============

  const loginWithPassword = async (email: string, password: string): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/auth/corporate/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, authMethod: 'email_password' })
      });

      const result = await response.json();
      
      if (result.success && result.user) {
        localStorage.setItem('corporate_token', result.token);
        setUser(result.user);
        setIsAuthenticated(true);
        
        toast({
          title: "Login Successful",
          description: `Welcome back, ${result.user.name}!`
        });
        
        return { success: true, user: result.user, token: result.token };
      } else {
        toast({
          title: "Login Failed",
          description: result.message || "Invalid email or password",
          variant: "destructive"
        });
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = "Network error occurred. Please try again.";
      toast({
        title: "Login Error",
        description: message,
        variant: "destructive"
      });
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithCode = async (email: string, code: string): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/auth/corporate/login-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });

      const result = await response.json();
      
      if (result.success && result.user) {
        localStorage.setItem('corporate_token', result.token);
        setUser(result.user);
        setIsAuthenticated(true);
        
        toast({
          title: "Login Successful",
          description: `Welcome, ${result.user.name}!`
        });
        
        return { success: true, user: result.user, token: result.token };
      } else {
        toast({
          title: "Login Failed",
          description: result.message || "Invalid or expired code",
          variant: "destructive"
        });
        return { success: false, message: result.message };
      }
    } catch (error) {
      const message = "Network error occurred. Please try again.";
      toast({
        title: "Login Error",
        description: message,
        variant: "destructive"
      });
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const requestAccessCode = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/corporate/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Access Code Sent",
          description: "Check your email for the 6-digit access code"
        });
      } else {
        toast({
          title: "Request Failed",
          description: result.message || "Could not send access code",
          variant: "destructive"
        });
      }
      
      return result;
    } catch (error) {
      const message = "Network error occurred. Please try again.";
      toast({
        title: "Request Error",
        description: message,
        variant: "destructive"
      });
      return { success: false, message };
    }
  };

  const acceptInvitation = async (token: string): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/auth/corporate/accept-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Invitation Accepted",
          description: "Welcome to the team!"
        });
      }
      
      return result;
    } catch (error) {
      return { success: false, message: "Network error occurred" };
    } finally {
      setIsLoading(false);
    }
  };

  const joinWithCode = async (code: string): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/auth/corporate/join-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Successfully Joined",
          description: `Welcome to ${result.corporateAccount?.companyName}!`
        });
      }
      
      return result;
    } catch (error) {
      return { success: false, message: "Network error occurred" };
    } finally {
      setIsLoading(false);
    }
  };

  const ssoLogin = async (provider: string) => {
    try {
      const response = await fetch(`${API_BASE}/sso/login/${provider}`, {
        method: 'GET'
      });

      const result = await response.json();
      
      if (result.success && result.redirectUrl) {
        // Redirect to SSO provider
        window.location.href = result.redirectUrl;
      }
      
      return result;
    } catch (error) {
      return { success: false, message: "SSO login failed" };
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('corporate_token');
      if (token) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('corporate_token');
      setUser(null);
      setIsAuthenticated(false);
      
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out"
      });
    }
  };

  // ============== CORPORATE MANAGEMENT ==============

  const createInvitation = async (email: string, role: string, department?: string): Promise<Invitation> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/invitations`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify({ email, role, department })
    });

    const result = await response.json();
    
    if (!result.success) {
      // Throw so callers can display the real error message
      throw new Error(result.message || 'Failed to send invitation');
    }
    
    return result.invitation;
  };

  const generateJoinCode = async (role: string, department?: string, options?: any): Promise<JoinCode> => {
    const token = localStorage.getItem('corporate_token');
    const response = await fetch(`${API_BASE}/corporate/join-codes`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify({ role, department, ...options })
    });

    const result = await response.json();
    return result.joinCode;
  };

  const manageBilling = async (action: string, params?: any): Promise<BillingResult> => {
    const token = localStorage.getItem('corporate_token');
    const response = await fetch(`${API_BASE}/corporate/billing/${action}`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify(params)
    });

    return await response.json();
  };

  const manageUsers = async (action: string, userIds?: string[], data?: any): Promise<UserResult> => {
    const token = localStorage.getItem('corporate_token');
    const response = await fetch(`${API_BASE}/corporate/users/${action}`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify({ userIds, ...data })
    });

    return await response.json();
  };

  const deactivateUser = async (userId: string) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/users/${userId}/deactivate`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await response.json();
  };

  const activateUser = async (userId: string) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/users/${userId}/activate`, {
      method: 'PATCH',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await response.json();
  };

  const deleteUser = async (userId: string) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    return await response.json();
  };

  // ============== UTILITY FUNCTIONS ==============

  const getCorporateAccount = async (): Promise<CorporateAccount | null> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/account`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.corporateAccount;
    }
    return null;
  };

  const updateCorporateSettings = async (settings: any) => {
    const token = localStorage.getItem('corporate_token');
    const response = await fetch(`${API_BASE}/corporate/settings`, {
      method: 'PUT',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      },
      body: JSON.stringify(settings)
    });

    return await response.json();
  };

  const getInvitations = async (): Promise<Invitation[]> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/invitations`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.invitations || [];
    }
    return [];
  };

  const deleteInvitation = async (id: string): Promise<void> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/invitations/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.message || 'Failed to delete invitation');
    }
  };

  const getJoinCodes = async (): Promise<JoinCode[]> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/join-codes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.joinCodes || [];
    }
    return [];
  };

  const getUsersList = async (): Promise<CorporateUser[]> => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/corporate/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.ok) {
      const result = await response.json();
      return result.users || [];
    }
    return [];
  };

  // ============== PERMISSION CHECKING ==============

  const hasPermission = (resource: string, action: string): boolean => {
    if (!user || !user.permissions) return false;
    
    const permission = user.permissions.find(p => p.resource === resource);
    return permission ? permission.actions.includes(action) : false;
  };

  const isRole = (role: string): boolean => {
    return user?.role === role;
  };

  const canManageUsers = (): boolean => {
    return ['super_admin', 'corporate_owner', 'limited_admin', 'admin'].includes(user?.role || '');
  };

  const canManageBilling = (): boolean => {
    return ['super_admin', 'corporate_owner'].includes(user?.role || '');
  };

  const canCreateTeams = (): boolean => {
    return ['super_admin', 'corporate_owner', 'admin', 'manager'].includes(user?.role || '');
  };

  // Context value
  const contextValue: CorporateAuthContextType = {
    // Authentication State
    user,
    isAuthenticated,
    isLoading,
    
    // Authentication Methods
    loginWithPassword,
    loginWithCode,
    requestAccessCode,
    acceptInvitation,
    joinWithCode,
    ssoLogin,
    logout,
    
    // Corporate Management
    createInvitation,
    generateJoinCode,
    manageBilling,
    manageUsers,
    deactivateUser,
    activateUser,
    deleteUser,
    
    // Corporate Account Management
    getCorporateAccount,
    updateCorporateSettings,
    getInvitations,
    deleteInvitation,
    getJoinCodes,
    getUsersList,
    
    // Permissions & Role Checking
    hasPermission,
    isRole,
    canManageUsers,
    canManageBilling,
    canCreateTeams,
  };

  return (
    <CorporateAuthContext.Provider value={contextValue}>
      {children}
    </CorporateAuthContext.Provider>
  );
};

// Custom hook to use Corporate Authentication Context
export const useCorporateAuth = (): CorporateAuthContextType => {
  const context = useContext(CorporateAuthContext);
  if (!context) {
    throw new Error('useCorporateAuth must be used within a CorporateAuthProvider');
  }
  return context;
};

export default CorporateAuthContext;